import { encodeBase64, generateKeyPair } from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";

/**
 * Ensure an X25519 keypair exists in local storage, generating one on
 * first run. Idempotent — subsequent calls return the existing public
 * key without touching storage.
 *
 * The keys are stored base64-encoded so they round-trip through
 * IndexedDB and the BroadcastChannel without any binary-blob
 * serialisation concerns. Decoding happens at encrypt/decrypt time.
 *
 * Returns the base64-encoded public key — the only piece the rest of
 * the flow needs (server pairing sends just the public key; the
 * secret stays here).
 *
 * Concurrency: two callers arriving in the same tick (React StrictMode
 * double-mount, or two components firing pairing setup together) both
 * see `existing.publicKey === undefined` before either write lands —
 * left unguarded, each generates a fresh pair and the second write
 * wins, leaving the first caller's public key stale against the
 * stored secret. The module-level `inflight` promise de-dupes: the
 * first call installs it, every concurrent caller awaits the same
 * promise, and it clears on settle so subsequent unrelated calls
 * proceed normally.
 */
let inflight: Promise<string> | null = null;

export function ensureKeypair(): Promise<string> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const existing = await webStorage.get("publicKey", "secretKey");
      if (existing.publicKey && existing.secretKey) {
        return existing.publicKey;
      }

      const keyPair = generateKeyPair();
      const publicKey = encodeBase64(keyPair.publicKey);
      const secretKey = encodeBase64(keyPair.secretKey);
      await webStorage.set({ publicKey, secretKey });
      return publicKey;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
